// apps/frontend/src/components/notifications/SaleToast.tsx
// 🎉 Toast dopamine — nouvelle vente confirmée
// Vente normale : bannière animée en haut à droite
// Première vente : overlay confetti plein écran

import React, { useEffect, useRef, useState } from 'react';
import { glowpNavigate } from '../../lib/navigate';
import {
  X,
  TrendingUp,
  ShoppingBag,
  PartyPopper,
  Sparkles,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SaleData {
  orderId: string;
  orderNumber?: string;
  productTitle: string;
  netAmount: number;
  isFirstSale: boolean;
}

interface SaleToastProps {
  sale: SaleData | null;
  onDismiss: () => void;
}

// ─── Confetti (CSS-only) ──────────────────────────────────────────────────────

const CONFETTI_COLORS = [
  'var(--brand-500)',
  '#F5C842',
  '#E87C5A',
  '#7BC47A',
  '#6BAED6',
  '#fff',
];

function ConfettiPiece({ index }: { index: number }) {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = `${(index * 7.3 + 3) % 100}%`;
  const size = 6 + (index % 6);
  const delay = `${(index * 0.13) % 2}s`;
  const duration = `${2.2 + (index % 8) * 0.3}s`;
  const isCircle = index % 3 === 0;

  return (
    <div
      style={{
        position: 'absolute',
        top: '-20px',
        left,
        width: size,
        height: isCircle ? size : size * 0.5,
        borderRadius: isCircle ? '50%' : '2px',
        backgroundColor: color,
        animation: `confettiFall ${duration} ${delay} ease-in forwards`,
        transform: `rotate(${index * 37}deg)`,
      }}
    />
  );
}

// ─── Overlay première vente ───────────────────────────────────────────────────

const FirstSaleOverlay: React.FC<{ sale: SaleData; onDismiss: () => void }> = ({
  sale,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Micro-delay pour laisser le DOM rendre avant l'animation
    const t = setTimeout(() => setVisible(true), 30);
    // Haptic feedback sur mobile
    if ('vibrate' in navigator) navigator.vibrate([100, 50, 200]);
    return () => clearTimeout(t);
  }, []);

  const handleViewOrder = () => {
    onDismiss();
    glowpNavigate(`/orders/${sale.orderId}`);
  };

  const handleSellMore = () => {
    onDismiss();
    // Déclenche le flow "vendre" via l'événement global
    window.dispatchEvent(new CustomEvent('glowp:openSell'));
  };

  const amountStr = sale.netAmount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });

  return (
    <>
      {/* Styles confetti */}
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0; }
        }
        @keyframes saleOverlayIn {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes amountPop {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[999998] flex items-center justify-center p-4"
        style={{
          backgroundColor: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(6px)',
        }}
        onClick={onDismiss}
      >
        {/* Confetti */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 60 }, (_, i) => (
            <ConfettiPiece key={i} index={i} />
          ))}
        </div>

        {/* Card */}
        <div
          className="relative bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
          style={{
            animation: visible
              ? 'saleOverlayIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards'
              : 'none',
            opacity: visible ? undefined : 0,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 p-1.5 rounded-full text-brand-muted hover:text-brand-warm hover:bg-brand-light transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon */}
          <div className="mb-4">
            <PartyPopper className="w-14 h-14 text-brand-500 mx-auto" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-brand-dark mb-1">
            Ta première vente !
          </h2>
          <p className="text-brand-warm text-sm mb-1">
            "{sale.productTitle}" vient d'être vendu
          </p>
          <p className="text-sm font-medium mb-6 text-brand-500">
            Et ce n'est que le début{' '}
            <Sparkles className="w-3.5 h-3.5 inline text-brand-500" />
          </p>

          {/* Amount */}
          <div
            className="rounded-2xl py-4 px-6 mb-6 inline-block"
            style={{
              backgroundColor: '#F5F0E8',
              animation:
                'amountPop 0.5s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) both',
            }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-1 text-brand-500">
              Tu gagnes
            </p>
            <p className="text-4xl font-bold" style={{ color: '#B89968' }}>
              {amountStr}
            </p>
            <p className="text-xs text-brand-muted mt-1">
              net après commission
            </p>
          </div>

          {/* Total cumulé */}
          <p className="text-xs text-brand-muted mb-6">
            Total gagné :{' '}
            <span className="font-semibold" style={{ color: '#B89968' }}>
              {amountStr}
            </span>
          </p>

          {/* CTAs */}
          <div className="flex gap-3">
            <button
              onClick={handleViewOrder}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-semibold text-white text-sm transition-all hover:shadow-lg active:scale-95 bg-brand-500"
            >
              <ShoppingBag className="w-4 h-4" />
              Voir la commande
            </button>
            <button
              onClick={handleSellMore}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-semibold text-sm transition-all hover:shadow-md active:scale-95"
              style={{ backgroundColor: '#F5F0E8', color: 'var(--brand-500)' }}
            >
              <TrendingUp className="w-4 h-4" />
              Vendre encore
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Toast vente normale ──────────────────────────────────────────────────────

const SaleBanner: React.FC<{ sale: SaleData; onDismiss: () => void }> = ({
  sale,
  onDismiss,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, 6000);
    return () => clearTimeout(timerRef.current);
  }, [onDismiss]);

  const amountStr = sale.netAmount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });

  return (
    <>
      <style>{`
        @keyframes saleBannerIn {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>

      <div
        className="fixed top-20 right-4 z-[999997] max-w-xs w-full rounded-2xl shadow-2xl overflow-hidden cursor-pointer"
        style={{
          animation:
            'saleBannerIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
        }}
        onClick={() => {
          onDismiss();
          glowpNavigate(`/orders/${sale.orderId}`);
        }}
      >
        {/* Barre couleur */}
        <div className="h-1 w-full bg-brand-500" />

        <div className="bg-white px-4 py-3 flex items-start gap-3">
          {/* Icône */}
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl"
            style={{ backgroundColor: '#F5F0E8' }}
          >
            💸
          </div>

          {/* Texte */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-brand-dark text-sm leading-tight">
              Nouvelle vente !
            </p>
            <p className="text-xs text-brand-warm mt-0.5 truncate">
              "{sale.productTitle}"
            </p>
            <p className="text-sm font-bold mt-1 text-brand-500">
              + {amountStr} net
            </p>
          </div>

          {/* Close */}
          <button
            onClick={e => {
              e.stopPropagation();
              onDismiss();
            }}
            className="flex-shrink-0 p-1 text-brand-border hover:text-brand-warm transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Export principal ─────────────────────────────────────────────────────────

const SaleToast: React.FC<SaleToastProps> = ({ sale, onDismiss }) => {
  if (!sale) return null;

  if (sale.isFirstSale) {
    return <FirstSaleOverlay sale={sale} onDismiss={onDismiss} />;
  }

  return <SaleBanner sale={sale} onDismiss={onDismiss} />;
};

export default SaleToast;
